import React, {useState} from "react";
import { useTranslation } from 'react-i18next';

import '../assets/styles/Tools.scss';
import UUIDGenerator from "./Tools/UUIDGenerator";
import TimestampConverter from "./Tools/TimestampConverter";
import CountryCodeConverter from "./Tools/CountryCodeConverter";
import UnitConverter from "./Tools/UnitConverter";
import Base64Encoder from "./Tools/Base64Encoder";
import Base64Decoder from "./Tools/Base64Decoder";
import URLConverter from "./Tools/URLConverter";
import QRGenerator from "./Tools/QRGenerator";
import CompoundInterestCalculator from "./Tools/CompoundInterestCalculator";
import HTTPErrorConverter from "./Tools/HTTPErrorConverter";
import JWTDecoder from "./Tools/JWTDecoder";
import FontPreview from "./Tools/FontPreview";
import WebLoadAnalyzer from "./Tools/WebLoadAnalyzer";

interface ToolEntry {
    id: string;
    name: string;
    component: React.ReactNode;
}

function Tools() {
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>("");
    const [activeId, setActiveId] = useState<string>(() => window.location.hash.slice(1) || 'timestamp');

    const sections: { id: string; title: string; tools: ToolEntry[] }[] = [
        {
            id: 'coding',
            title: t('tools.sections.coding'),
            tools: [
                { id: 'timestamp', name: 'Timestamp Converter', component: <TimestampConverter /> },
                { id: 'country-codes', name: 'Country Code Converter', component: <CountryCodeConverter /> },
                { id: 'base64-encode', name: 'Base64 Encoder', component: <Base64Encoder /> },
                { id: 'base64-decode', name: 'Base64 Decoder', component: <Base64Decoder /> },
                { id: 'uuid', name: 'UUID Generator', component: <UUIDGenerator /> },
            ]
        },
        {
            id: 'data',
            title: t('tools.sections.data'),
            tools: [
                { id: 'qr', name: 'QR Generator', component: <QRGenerator /> },
                { id: 'units', name: 'Unit Converter', component: <UnitConverter /> },
            ]
        },
        {
            id: 'web',
            title: t('tools.sections.web'),
            tools: [
                { id: 'url', name: 'URL Encoder/Decoder', component: <URLConverter /> },
                { id: 'http-errors', name: 'HTTP Error Converter', component: <HTTPErrorConverter /> },
                { id: 'web-load', name: 'Web Load Analyzer', component: <WebLoadAnalyzer /> },
                { id: 'fonts', name: 'Font Preview', component: <FontPreview /> },
            ]
        },
        {
            id: 'security',
            title: t('tools.sections.security'),
            tools: [
                { id: 'jwt', name: 'JWT Decoder', component: <JWTDecoder /> },
            ]
        },
        {
            id: 'finance',
            title: t('tools.sections.finance'),
            tools: [
                { id: 'compound-interest', name: 'Compound Interest Calculator', component: <CompoundInterestCalculator /> },
            ]
        }
    ];

    const allTools = sections.flatMap(section => section.tools.map(tool => ({ ...tool, sectionId: section.id, sectionTitle: section.title })));
    const activeTool = allTools.find(tool => tool.id === activeId) ?? allTools[0];

    const selectTool = (id: string) => {
        setActiveId(id);
        window.history.replaceState(null, '', `#${id}`);
    };

    const query = search.trim().toLowerCase();
    const visibleSections = sections
        .map(section => ({
            ...section,
            tools: section.tools.filter(tool => !query || tool.name.toLowerCase().includes(query))
        }))
        .filter(section => section.tools.length > 0);

    return(
        <div className="tools-dashboard" id="tools">
            <aside className="tools-sidebar">
                <h1 className="tools-sidebar__title">{t('tools.title')}</h1>
                <input
                    type="search"
                    className="tools-sidebar__search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('tools.search', { defaultValue: 'Search...' })}
                    aria-label={t('tools.search', { defaultValue: 'Search...' })}
                />
                <nav className="tools-sidebar__nav">
                    {visibleSections.map(section => (
                        <div key={section.id} className="tools-sidebar__group">
                            <span className="tools-sidebar__group-title">{section.title}</span>
                            {section.tools.map(tool => (
                                <button
                                    key={tool.id}
                                    type="button"
                                    className={`tools-sidebar__item${tool.id === activeTool.id ? ' active' : ''}`}
                                    aria-current={tool.id === activeTool.id ? 'page' : undefined}
                                    onClick={() => selectTool(tool.id)}
                                >
                                    {tool.name}
                                </button>
                            ))}
                        </div>
                    ))}
                    {visibleSections.length === 0 && (
                        <p className="tools-sidebar__empty">{t('tools.noResults', { defaultValue: 'No tools found' })}</p>
                    )}
                </nav>
            </aside>

            <main className="tools-panel">
                <header className="tools-panel__header">
                    <span className="tools-panel__category">{activeTool.sectionTitle}</span>
                    <h2 className="tools-panel__title">{activeTool.name}</h2>
                </header>
                <div className="tools-panel__body" key={activeTool.id}>
                    {activeTool.component}
                </div>
            </main>
        </div>
    );
}

export default Tools;
